import { QuoteServiceLineChangeReview } from './QuoteServiceLineChangeReview';
import { parseQuoteServiceLineReview } from './quoteServiceLinePayload';
import { QuoteServiceAddReview } from './QuoteServiceAddReview';
import { parseQuoteServiceAddReview } from './quoteServiceAddPayload';
import { useState } from 'react';
import { Box, Button, Card, CardContent, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { ConfirmationPresenter, ConfirmationRetry } from '@/stores/assistantStore';
import { WorkConfirmationReview } from './WorkConfirmationReview';
import { parseWorkReview } from './workReviewPayload';
import { QuoteConfirmationReview } from './QuoteConfirmationReview';
import { parseQuoteConfirmationReview } from './quoteConfirmationPayload';
import { QuoteSendConfirmationReview } from './QuoteSendConfirmationReview';
import { parseQuoteSendReview } from './quoteSendReviewContract';
import { QuoteItemChangeReview } from './QuoteItemChangeReview';
import { parseQuoteItemChangeReview } from './quoteItemChangePayload';

interface ConfirmationCardProps {
  /** The server's composed summary — the transcript row, and the fallback text. */
  description: string;
  toolName?: string | undefined;
  /**
   * The harness path's localised rendering (#2180): `t(key, params)` from the
   * `assistant` namespace. Absent on legacy cards.
   */
  presenter?: ConfirmationPresenter | undefined;
  params: Record<string, unknown>;
  isLoading: boolean;
  /** Set when the server refused the last confirm but kept the plan (#2821). */
  retry?: ConfirmationRetry | undefined;
  /** `fields` carries values the user set on the card (#2821), e.g. the confirmed quote receipt time. */
  onConfirm: (fields?: ConfirmFields) => void;
  onCancel: () => void;
}

/** Values the human set on the card itself; sent with Confirm, never taken from the model. */
export interface ConfirmFields {
  readonly approvalReceivedAt?: string;
}

const UNFILLED_PLACEHOLDER = /\{\{\s*[\w.]+\s*\}\}/;

/**
 * One field of an UPDATE plan, as `payload.fieldChanges` carries it (#2199):
 * `{ field, from, to }` with the ROUTE's field name and the CURRENT value
 * (`null` when the row has none). Rendered so the user confirming "update 1
 * field" sees WHICH field and WHAT value — a count alone is not informed
 * consent (review finding). Field labels come from `confirmation.fields.*`
 * with the raw name as the fallback, so an unlabelled field is still shown.
 */
interface FieldChange {
  field: string;
  /**
   * #3820: generated Tier B updates name the route field's type, and their
   * values keep it (a number, a boolean, a date). Absent on the hand-built
   * ports, whose values are always text.
   */
  type?: FieldType;
  from: FieldValue;
  /** `null` is a clear. */
  to: FieldValue;
}

type FieldValue = string | number | boolean | null;

const FIELD_TYPES = ['string', 'number', 'integer', 'boolean', 'enum', 'date', 'datetime', 'uuid'] as const;
type FieldType = (typeof FIELD_TYPES)[number];

/** A real calendar day as `YYYY-MM-DD`: 2027-02-31 does not roll over into March, it is refused. */
function isCalendarDay(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return !Number.isNaN(day.getTime()) && day.toISOString().slice(0, 10) === value;
}

const DATETIME = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(Z|[+-](\d{2}):?(\d{2}))$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * An instant in ISO form with `Z` or an offset, on a real calendar day and
 * clock time: 2027-02-31T12:00:00Z and a bare date are refused, not rolled over.
 */
function isInstant(value: string): boolean {
  const match = DATETIME.exec(value);
  if (!match || !isCalendarDay(match[1] ?? '')) return false;
  const at = (index: number): number => Number(match[index] ?? 0);
  return at(2) <= 23 && at(3) <= 59 && at(4) <= 59 && at(6) <= 23 && at(7) <= 59 && !Number.isNaN(Date.parse(value));
}

/** Whether a value is what its declared type carries (or a clear). Text only without a type. */
function valueFits(value: unknown, type: FieldType | undefined): value is FieldValue {
  if (value === null) return true;
  if (type === 'integer') return typeof value === 'number' && Number.isInteger(value);
  if (type === 'number') return typeof value === 'number' && Number.isFinite(value);
  if (type === 'boolean') return typeof value === 'boolean';
  if (type === 'date') return typeof value === 'string' && isCalendarDay(value);
  if (type === 'datetime') return typeof value === 'string' && isInstant(value);
  if (type === 'uuid') return typeof value === 'string' && UUID.test(value);
  return typeof value === 'string';
}

/**
 * Every significant digit, from the number's own shortest round-trip text:
 * the locale's grouping and decimal separator, no rounding. An exponent form
 * (1e-21) stays as it is, so 1e-21 and 2e-21 never look alike.
 */
function exactNumber(value: number, language: string): string {
  const raw = String(value);
  if (/e/i.test(raw)) return raw;
  const negative = raw.startsWith('-');
  const [whole = '0', fraction] = (negative ? raw.slice(1) : raw).split('.');
  const format = new Intl.NumberFormat(language);
  const decimal = format.formatToParts(1.5).find((part) => part.type === 'decimal')?.value ?? '.';
  return `${negative ? '-' : ''}${format.format(BigInt(whole))}${fraction === undefined ? '' : `${decimal}${fraction}`}`;
}

/** Date, time to the second (milliseconds when there are any) and the zone it is shown in. */
function instantText(value: string, language: string): string {
  const instant = new Date(value);
  return new Intl.DateTimeFormat(language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
    ...(instant.getUTCMilliseconds() !== 0 ? { fractionalSecondDigits: 3 as const } : {}),
  }).format(instant);
}

/** Locale formatting that never throws: anything it cannot format is shown as its raw text. */
function formatted(raw: string, format: () => string): string {
  try {
    return format();
  } catch {
    return raw;
  }
}

const FIELD_NAME = /^[A-Za-z][A-Za-z0-9]*$/;
/** Names that reach Object.prototype through the bundle lookup; never a real column. */
const RESERVED_FIELD_NAMES = new Set(['constructor', 'prototype', 'toString', 'valueOf', 'hasOwnProperty']);

/**
 * `absent`: the payload carries no diff (a CREATE) — the headline is the whole
 * card. `invalid`: it carries one this client cannot read (a malformed or
 * duplicated entry, an empty list). An UPDATE whose diff cannot be shown must
 * not be confirmable: a count without values is exactly what round one of
 * review refused, so the card fails closed and says so (review round 2).
 */
type ChangesOf =
  | { readonly kind: 'absent' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'diff'; readonly changes: readonly FieldChange[] };

/**
 * The payload key is deliberately specific (`fieldChanges`, not `changes`):
 * this card renders EVERY tool's pending payload, and a later port whose
 * payload happens to carry a `changes` array of another shape must not have
 * its Confirm disabled by this reader (review round 2).
 */
const FIELD_CHANGES_KEY = 'fieldChanges';

function changesOf(params: Record<string, unknown>): ChangesOf {
  if (!(FIELD_CHANGES_KEY in params)) return { kind: 'absent' };
  const raw = params[FIELD_CHANGES_KEY];
  if (!Array.isArray(raw) || raw.length === 0) return { kind: 'invalid' };
  const changes: FieldChange[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    if (!item || typeof item !== 'object') return { kind: 'invalid' };
    const { field, from, to, type } = item as Record<string, unknown>;
    if (typeof field !== 'string' || !FIELD_NAME.test(field) || RESERVED_FIELD_NAMES.has(field) || seen.has(field)) {
      return { kind: 'invalid' };
    }
    if (type !== undefined && !(FIELD_TYPES as readonly unknown[]).includes(type)) return { kind: 'invalid' };
    const declared = type as FieldType | undefined;
    if (!valueFits(from, declared) || !valueFits(to, declared)) return { kind: 'invalid' };
    seen.add(field);
    changes.push({ field, from, to, ...(declared ? { type: declared } : {}) });
  }
  return { kind: 'diff', changes };
}

function valueText(t: TFunction<'assistant'>, language: string, value: FieldValue, type?: FieldType): string {
  if (value === null) return t('confirmation.noValue');
  if (typeof value === 'boolean') return t(value ? 'confirmation.booleanTrue' : 'confirmation.booleanFalse');
  // Full precision: Intl's default rounding would show 1.2341 and 1.2344 alike.
  if (typeof value === 'number') return formatted(String(value), () => exactNumber(value, language));
  if (value.trim() === '') return t('confirmation.blankValue');
  // A calendar day is shown as that day, never shifted by the viewer's zone.
  if (type === 'date')
    return formatted(value, () => new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`)));
  if (type === 'datetime') return formatted(value, () => instantText(value, language));
  return value;
}

function changeText(t: TFunction<'assistant'>, language: string, change: FieldChange): string {
  const field = t(`confirmation.fields.${change.field}`, { defaultValue: change.field });
  const from = valueText(t, language, change.from, change.type);
  const to = valueText(t, language, change.to, change.type);
  return t('confirmation.change', {
    replace: { field, from, to },
    defaultValue: `${field}: ${from} → ${to}`,
  });
}

function presentOrFallback(
  t: TFunction<'assistant'>,
  presenter: ConfirmationPresenter,
  description: string
): string {
  const rendered: unknown = t(presenter.key, {
    replace: presenter.params,
    defaultValue: description,
    returnObjects: true,
  });
  if (typeof rendered !== 'string') return description;
  if (UNFILLED_PLACEHOLDER.test(rendered)) return description;
  return rendered;
}

export function ConfirmationCard({
  description,
  toolName,
  presenter,
  params,
  isLoading,
  retry,
  onConfirm,
  onCancel,
}: ConfirmationCardProps) {
  const { t, i18n } = useTranslation('assistant');
  const { t: tc } = useTranslation('common');

  // Nothing but a finished translation or the server's summary may reach the
  // screen. Three guards, each for a case a well-typed presenter cannot rule
  // out on its own:
  //  - `replace` carries the params, so a param named like an i18next option
  //    (`lng`, `count`, `context`, `returnDetails`, `defaultValue_other`…)
  //    interpolates instead of steering the lookup;
  //  - `defaultValue` is what i18next returns for a key in no bundle
  //    (fallback language included) — the unknown-key case;
  //  - a non-string result (a parent key with `returnObjects`) or a
  //    placeholder the params did not fill (`{{lastName}}`) is not a
  //    translation, and falls back to the summary rather than showing.
  const text = presenter ? presentOrFallback(t, presenter, description) : description;
  const diff = changesOf(params);
  const changes = diff.kind === 'diff' ? diff.changes : [];
  const diffInvalid = diff.kind === 'invalid';
  const quoteReview = parseQuoteConfirmationReview(params, presenter?.key);
  const workReview = parseWorkReview(params, presenter?.key, toolName);
  const sendReview = parseQuoteSendReview(params, presenter?.key);
  const serviceLineChange = parseQuoteServiceLineReview(params, presenter?.key, toolName);
  const serviceAdd = parseQuoteServiceAddReview(params, presenter?.key, toolName);
  const lineChange = parseQuoteItemChangeReview(params, presenter?.key);
  // A quote acceptance needs the receipt time the staff member confirms on
  // the card; Confirm stays disabled until it is valid (#2821).
  const [receivedAt, setReceivedAt] = useState<string | null>(null);
  const needsReceipt = quoteReview.kind === 'review' && quoteReview.review.action === 'accept';

  return (
    <Box sx={{ mb: 1.5, ml: 4.5 }}>
      <Card
        variant="outlined"
        sx={{
          maxWidth: '85%',
          borderColor: 'warning.main',
          borderWidth: 1,
        }}
      >
        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
          <Typography variant="caption" color="warning.main" fontWeight={600}>
            {t('confirmation.title')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, mb: 1.5 }}>
            {text}
          </Typography>
          {changes.length > 0 && (
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {changes.map((change) => (
                <Typography component="li" variant="body2" key={change.field}>
                  {changeText(t, i18n.language, change)}
                </Typography>
              ))}
            </Box>
          )}
          {(workReview.kind === 'ownTime' || workReview.kind === 'execution' || workReview.kind === 'timer') && (
            <WorkConfirmationReview value={workReview} />
          )}
          {workReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('workReview.unavailable')}
            </Typography>
          )}
          {quoteReview.kind === 'review' && (
            <QuoteConfirmationReview
              review={quoteReview.review}
              onReceiptChange={setReceivedAt}
              retry={retry}
            />
          )}
          {quoteReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{t('quoteReview.unavailable')}</Typography>
          )}
          {sendReview.kind === 'review' && <QuoteSendConfirmationReview review={sendReview.review} />}
          {sendReview.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('quoteSend.unavailable')}
            </Typography>
          )}
          {serviceLineChange.kind === 'review' && <QuoteServiceLineChangeReview review={serviceLineChange.review} />}
          {serviceAdd.kind === 'review' && <QuoteServiceAddReview review={serviceAdd.review} />}
          {serviceAdd.kind === 'invalid' && <Typography variant="body2" color="error">{t('quoteServiceAdd.unavailable')}</Typography>}
          {serviceLineChange.kind === 'invalid' && <Typography variant="body2" color="error">{t('quoteServiceLine.unavailable')}</Typography>}
          {lineChange.kind === 'review' && <QuoteItemChangeReview review={lineChange.review} />}
          {lineChange.kind === 'invalid' && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{t('quoteItemChange.unavailable')}</Typography>
          )}
          {retry?.reason === 'retryable' && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 1.5 }} role="status">
              {t('confirmation.retryable')}
            </Typography>
          )}
          {diffInvalid && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {t('confirmation.diffUnavailable')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() =>
                onConfirm(needsReceipt && receivedAt ? { approvalReceivedAt: receivedAt } : undefined)
              }
              disabled={
                isLoading ||
                diffInvalid ||
                quoteReview.kind === 'invalid' ||
                lineChange.kind === 'invalid' ||
                serviceLineChange.kind === 'invalid' ||
                serviceAdd.kind === 'invalid' ||
                workReview.kind === 'invalid' ||
                sendReview.kind === 'invalid' ||
                (needsReceipt && receivedAt === null)
              }
            >
              {retry?.reason === 'retryable' ? t('confirmation.retryButton') : tc('confirm')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CancelOutlinedIcon />}
              onClick={onCancel}
              disabled={isLoading}
            >
              {tc('cancel')}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
